import React, { useState, useEffect, useContext, } from 'react';
import axios from 'axios';
import { FaSistrix, FaPlay } from "react-icons/fa6";
import Pagination from '../layout/pagination.js';
import { AuthContext } from '../auth/authToken.js';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    useDisclosure,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

export default function Gallery() {
    const history = useHistory();
    const [token, setToken] = useState("");
    const { isLoggedIn } = useContext(AuthContext);
    const [galleryData, setGalleryData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(6);
    const { isOpen: isDetailModalOpen, onOpen: onOpenDetailModal, onClose: onCloseDetailModal } = useDisclosure();

    // Main API configuration
    const apiMain = "https://bbyshin-backend.onrender.com/api"; // Main API configuration


    useEffect(() => {
        fetchInstagramPosts();
    }, []);

    const fetchInstagramPosts = async () => {
        try {
            const response = await axios.get(`${apiMain}/instagram-feed`);
            const posts = response.data.posts; // Access the posts array from the response
    
            // Map the posts to extract the necessary data
            const formattedPosts = posts.map(post => ({
                id: post.instagramId,
                ...post.postData, // Spread the postData object
                article: post.article // Include the article object
            }));
    
            // Sort the posts by timestamp in descending order (newest first)
            const sortedPosts = formattedPosts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
            setGalleryData(sortedPosts); // Set the sorted data to galleryData
        } catch (error) {
            console.error("Error fetching Instagram data:", error);
            setGalleryData([]); // Set an empty array on error
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const filteredData = Array.isArray(galleryData) ? galleryData.filter(item => {
        if (!item.article.title) return false;
        const first50Words = item.article.title.split(' ').slice(0, 50).join(' ');
        return first50Words.toLowerCase().includes(searchTerm.toLowerCase());
    }) : [];

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setItemsPerPage(9);
            } else if (window.innerWidth >= 640) {
                setItemsPerPage(8);
            } else {
                setItemsPerPage(6);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
    };

    const handleItemClick = (item) => {
        history.push(`/gallery/${item.id}`, { item });
    }

    return (
        <main className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen w-full">
            <section className='w-full min-h-screen p-4 sm:p-8 md:p-16 lg:p-24 flex flex-col gap-6 sm:gap-8 lg:gap-10'>
                <header className='w-full flex justify-center'>
                    <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-[PoppinsB] text-gray-800 tracking-wide'>GALLERY</h1>
                </header>
                <div className='flex flex-row items-center justify-center'>
                    <div className='flex flex-row items-center shadow-lg hover:shadow-xl transition-shadow duration-300 rounded-xl'>
                        <span className='bg-[#fcfc00] bg-opacity-90 rounded-l-xl px-3 sm:px-4 h-10 sm:h-12 flex items-center'>
                            <FaSistrix className='w-4 h-4 sm:w-5 sm:h-5 text-gray-700' />
                        </span>
                        <input
                            onChange={handleSearch}
                            className='bg-white rounded-r-xl h-10 sm:h-12 w-48 sm:w-64 px-3 sm:px-4 py-2 focus:outline-none border-2 border-[#fcfc00] border-opacity-90'
                            placeholder='Search gallery...'
                            type="search"
                        />
                    </div>
                </div>
                {filteredData.length === 0 ? (
                    <div className='w-full h-48 sm:h-64 flex justify-center items-center'>
                        <p className='text-lg sm:text-xl text-gray-600'>No results found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                        {currentItems.map((item) =>
                            <div
                                key={item.id}
                                className="group bg-white backdrop-blur-sm bg-opacity-90 rounded-xl overflow-hidden shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                                onClick={() => handleItemClick(item)}
                            >
                                <div className="relative">
                                    {item.media_type === 'VIDEO' ? (
                                        <div>
                                            <span className='absolute flex items-center justify-center w-full h-full bg-black bg-opacity-30'>
                                                <FaPlay className='w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 text-white'></FaPlay>
                                            </span>
                                            <img
                                            className="aspect-square object-cover w-full"
                                            src={item.thumbnail_url}
                                            alt="Instagram post"
                                        />
                                        </div>
                                    ) : (
                                        <img
                                            className="aspect-square object-cover w-full"
                                            src={item.media_url}
                                            alt="Instagram post"
                                        />
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-16 sm:h-20" />
                                </div>
                                <div className='p-3 sm:p-5'>
                                    <div className='flex justify-between items-center mb-2 sm:mb-3'>
                                        <span className='px-2 sm:px-3 py-1 bg-[#fcfc00] bg-opacity-90 rounded-full text-xs font-medium hover:bg-opacity-100 transition-all'>
                                            {item.media_type}
                                        </span>
                                        <span className='text-xs sm:text-sm text-gray-600'>
                                            {formatDate(item.timestamp)}
                                        </span>
                                    </div>
                                    <h3 className='font-semibold text-base sm:text-lg line-clamp-2 group-hover:text-[#e6e600] transition-colors duration-200'>
                                        {item.article.title}
                                    </h3>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className='flex items-center justify-center mt-4 sm:mt-8'>
                    <Pagination id="pagination" itemsPerPage={itemsPerPage} totalItems={filteredData.length} paginate={paginate} currentPage={currentPage} />
                </div>
            </section>
        </main>
    );
}
